import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const URL = "https://coastsidelaw.com.au/fixed-price-will-solicitor";

const FAQS = [
  {
    q: "How much does a solicitor-drafted will cost?",
    a: "A simple will is from $660 including GST, and an enduring power of attorney is from $440 including GST. A testamentary trust will starts at $2,200 including GST. Wills involving a blended family, a business or a trust are quoted before we start.",
  },
  {
    q: "What is the cost of making a will with a solicitor in Queensland?",
    a: "Fees vary between firms. Many quote by the hour or give a range, so the cost isn't clear until the bill arrives. Our price is fixed before we start: from $660 including GST for a simple will, from $440 for an enduring power of attorney, and from $2,200 for a will with a testamentary trust.",
  },
  {
    q: "How do I make a will with a solicitor?",
    a: "Call or send the enquiry form. We take your instructions at one appointment, in person, by phone or by video. You get a draft to check within a few days, then sign at a supervised appointment so the will is witnessed correctly.",
  },
  {
    q: "Is a will kit legal in Queensland?",
    a: "Yes, a will kit can be valid if it's signed and witnessed correctly. The problems are usually in the wording, the witnessing, and assets the kit doesn't deal with, such as jointly owned property, superannuation and family trusts. Those mistakes only come to light after you've died.",
  },
  {
    q: "What does a solicitor will cover that a kit doesn't?",
    a: "Advice on how your assets actually pass, including joint tenancy, super and life insurance. We check who could make a claim against your estate, include backup gifts if a beneficiary dies first, and supervise the signing so it's valid.",
  },
  {
    q: "How long does it take?",
    a: "Usually one appointment to take instructions, a draft within a few days, and a signing appointment. Home and hospital visits can be arranged.",
  },
  {
    q: "Can you redo my will kit or an old will?",
    a: "Yes. Bring your existing will or kit and we'll tell you what needs changing, at the same fixed price.",
  },
  {
    q: "Is a home or hospital will the same price?",
    a: "Home, hospital and aged-care wills cost a fixed $880 including GST, with no additional travel fee. If a medical report is needed to help establish capacity, the report charges are additional. Tell us where you are and whether the matter is urgent when you enquire.",
  },
];

export const Route = createFileRoute("/fixed-price-will-solicitor")({
  head: () => ({
    meta: [
      { title: fitTitle("Cost of Making a Will With a Solicitor: From $660 | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "The cost of making a will with a solicitor in QLD, fixed before you start: from $660 incl. GST for a simple will, from $440 for a power of attorney. Safer than a DIY will kit.",
        ),
      },
      { property: "og:title", content: "Solicitor-drafted wills from $660" },
      { property: "og:description", content: fitDescription("The safer alternative to a will kit, at a price fixed before you start.") },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Wills · Fixed price"
      title="The cost of making a will with a solicitor: from $660"
      lead="Making a will with a solicitor costs from $660 including GST at Coastside Law, agreed before we start. A DIY will kit looks cheap until something goes wrong. For that price, a Queensland solicitor drafts your will, checks how your property and super will actually pass, and supervises the signing."
      ctaLabel="Book my fixed-price will"
      boxTitle="Solicitor wills from $660"
      boxItems={[
        "A will drafted by a solicitor, not a template",
        "Advice on joint property, superannuation and life insurance",
        "Backup gifts if a beneficiary dies before you",
        "Supervised signing, so it's valid",
        "Enduring power of attorney: from $440 incl. GST extra",
      ]}
      faqs={FAQS}
      links={[
        { to: "/guides/diy-will-kit-vs-solicitor-drafted-will-queensland", label: "Will kit vs solicitor will" },
        { to: "/wills-and-estates", label: "Wills and estates" },
        { to: "/testamentary-trusts", label: "Testamentary trusts" },
        { to: "/fees", label: "Will fees and what is included" },
        { to: "/home-hospital-wills-sunshine-coast", label: "Home, hospital and aged-care wills" },
        { to: "/wills-estate-lawyers-sunshine-coast", label: "Sunshine Coast wills and estate lawyers" },
      ]}
      formHeading="Book a fixed-price will"
      ctaTitle="Ready to get it done properly?"
      ctaBody={`Call ${FIRM.phone}. We'll confirm your fixed price before we start.`}
    />
  ),
});
